/**
 * Which field is stopping a visit from being saved.
 *
 * beaut-558. `addVisit` marked the form touched and then returned on
 * `form.invalid` without a word, so pressing Save did nothing visible. The
 * check immediately below it — "no procedure chosen" — has always said so out
 * loud; only this one was silent.
 *
 * Marking the form touched does not help the customer field either: the
 * control the operator sees is `[ngModelOptions]="{ standalone: true }"` in
 * user-card, so it is not in this form at all and can never render the error
 * that `userId` is carrying. Naming the field in a message is what reaches
 * the operator, which is why this returns a key rather than trying to paint
 * a control.
 *
 * Order matters: the first invalid field in this list is the one named, and
 * the list runs top to bottom down the form as the operator sees it.
 */

/** Form controls that stop a save, in the order they appear on screen. */
const REQUIRED_FIELDS: ReadonlyArray<{ control: string; key: string }> = [
  { control: 'userId', key: 'visit.missing.customer' },
  { control: 'bookingDate', key: 'visit.missing.date' },
  { control: 'startTime', key: 'visit.missing.startTime' },
  { control: 'startTimeDate', key: 'visit.missing.startTime' },
  { control: 'endTime', key: 'visit.missing.endTime' },
  { control: 'endTimeDate', key: 'visit.missing.endTime' },
];

/** Shown when the form is invalid for a reason not in the list above. */
const FALLBACK_KEY = 'visit.missing.unknown';

export interface FormLike {
  get(control: string): { invalid?: boolean } | null;
}

/**
 * The translation key for the first field blocking the save, or a general
 * message when nothing in the list is to blame — a cross-field rule, for
 * instance, such as the end time preceding the start.
 */
export function missingVisitFieldKey(form: FormLike | null | undefined): string {
  if (!form) {
    return FALLBACK_KEY;
  }

  const blocking = REQUIRED_FIELDS.find(({ control }) => form.get(control)?.invalid === true);

  return blocking ? blocking.key : FALLBACK_KEY;
}
